import { useMemo } from 'react'

import { buildChartFromTable } from '../utils/chartUtils'
import { getMetricDisplayLabel } from '../utils/collectionMetrics'
import { getChartColSpanClass, getDefaultChartName } from '../utils/constants'
import { useCustomReportStore } from '../store'

import ChartResult from './ChartResult'
import ChartSettingsPopover from './ChartSettingsPopover'

function ChartPanel({ chart }) {
  const {
    config: { value: config },
    table: { data: tableData, status: tableStatus },
    charts: { data: chartsData },
  } = useCustomReportStore()

  const entry = chartsData[chart.id]
  const data = entry?.data ?? tableData
  const groupby = entry?.groupby ?? config.groupby

  const chartResult = useMemo(
    () => buildChartFromTable(data, chart, groupby, config.collection),
    [data, chart, groupby, config.collection],
  )

  const unsupportedLabels = useMemo(
    () => (chartResult.unsupportedKeys || []).map(key => getMetricDisplayLabel(config.collection, { key })),
    [chartResult.unsupportedKeys, config.collection],
  )

  const chartTitle = useMemo(() => {
    if (chart.name?.trim()) return chart.name.trim()
    return getDefaultChartName(config, chart.metricKeys)
  }, [chart.name, chart.metricKeys, config])

  return (
    <div className="rounded-sm border twy-border-base twy-bg-base-accent p-4 h-full flex flex-col">
      <div className="flex items-center justify-between gap-3 mb-3 shrink-0">
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-semibold twy-text-base">
            {chartTitle}
          </div>
        </div>
        <div className="shrink-0">
          <ChartSettingsPopover chart={chart} />
        </div>
      </div>

      {unsupportedLabels.length > 0 && <div className="mb-3 rounded-sm px-3 py-2 text-sm twy-text-danger twy-bg-base">
        {unsupportedLabels.join(', ')} cannot be summed across the group by levels left out of this chart. Add the missing levels or pick an additive metric.
      </div>}

      {tableStatus !== 'loaded' ? <div className="font-semibold py-8 text-center twy-text-base-muted">
        Run the report to generate chart data from the table.
      </div> : <ChartResult
        dataType={chart.dataType}
        result={chartResult}
        collection={config.collection}
      />
      }
    </div>
  )
}

export default function ChartsSection() {
  const { charts: { items } } = useCustomReportStore()

  if (!items.length) return null

  return (
    <div className="mb-4 grid grid-cols-12 gap-4 grid-flow-dense">
      {items.map(chart => <div key={chart.id} className={getChartColSpanClass(chart.layoutSize)}>
        <ChartPanel chart={chart} />
      </div>)}
    </div>
  )
}
